import React, { useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import api from "../utils/api";

import { formatRupiah } from "../utils/currency";
import { categoryLabel } from "../utils/categories";
import { mapProduct } from "../utils/productMapping";

const STORE_NAME = "Hyperstore";


const StarRow = ({ rate = 0, count = 0 }) => {
  const r = Math.max(0, Math.min(5, Number(rate || 0)));
  const full = Math.round(r); // mobile: simple & clean

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <svg
              key={i}
              viewBox="0 0 20 20"
              className={`h-3.5 w-3.5 ${
                i + 1 <= full ? "text-amber-300" : "text-white/15"
              }`}
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.173c.969 0 1.371 1.24.588 1.81l-3.376 2.454a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.538 1.118l-3.376-2.454a1 1 0 00-1.176 0l-3.376 2.454c-.783.57-1.838-.196-1.538-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.173a1 1 0 00.95-.69l1.286-3.967z" />
            </svg>
          ))}
        </div>
        <span className="text-xs font-semibold text-zinc-200">{r.toFixed(1)}</span>
      </div>
      <span className="text-[11px] text-zinc-500">{count} review</span>
    </div>
  );
};

const Products = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wishlistIds, setWishlistIds] = useState([]);

  const [activeCat, setActiveCat] = useState("all");
  const [activeType, setActiveType] = useState("all"); // all | digital | physical
  const [flashSaleOnly, setFlashSaleOnly] = useState(false);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("popular"); // popular | price_asc | price_desc | rating

  useEffect(() => {
    const controller = new AbortController();

    const getProducts = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${process.env.REACT_APP_API_URL || "https://apistore.iqbaldeveloper.my.id/api"}/products`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error("Failed to fetch products");
        const json = await res.json();
        
        // Laravel paginates products, so the array is in json.data
        const productsData = json.data || json;
        const mappedData = productsData.map(mapProduct);
        setData(mappedData);
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.error(err);
          toast.error("Gagal mengambil produk");
        }
      } finally {
        setLoading(false);
      }
    };

    getProducts();

    if (isAuthenticated) {
      api.get('/api/wishlists')
        .then(res => setWishlistIds(res.data.map(w => w.product_id)))
        .catch(err => console.log(err));
    }

    return () => controller.abort();
  }, [isAuthenticated]);

  const toggleWishlist = async (productId, e) => {
    e.preventDefault();
    if (!isAuthenticated) return toast.error("Silakan login untuk menyimpan wishlist");
    
    const isFavorited = wishlistIds.includes(productId);
    try {
      if (isFavorited) {
        await api.delete(`/api/wishlists/${productId}`);
        setWishlistIds(prev => prev.filter(id => id !== productId));
        toast.success("Dihapus dari wishlist");
      } else {
        await api.post('/api/wishlists', { product_id: productId });
        setWishlistIds(prev => [...prev, productId]);
        toast.success("Ditambahkan ke wishlist");
      }
    } catch (err) {
      toast.error("Gagal mengupdate wishlist");
    }
  };

  const categories = useMemo(() => {
    // Extract unique categories from loaded data, ensure no blanks
    const uniqueCats = [...new Set(data.map((item) => item.category).filter(Boolean))].sort();

    // Map into required format, with "all" at the front
    return [
      { key: "all", label: "Semua" },
      ...uniqueCats.map(cat => ({ key: cat, label: categoryLabel(cat) }))
    ];
  }, [data]);

  const filteredProducts = useMemo(() => {
    const q = query.trim().toLowerCase();

    const filtered = data.filter((item) => {
      const matchCat = activeCat === "all" || item.category === activeCat;
      const matchType = activeType === "all" || item.product_type === activeType;
      const matchFlashSale = !flashSaleOnly || (item.active_flash_sale !== null && item.active_flash_sale !== undefined);
      const matchName = q.length === 0 || item.title?.toLowerCase().includes(q);
      
      return matchCat && matchType && matchFlashSale && matchName;
    });

    return [...filtered].sort((a, b) => {
      // Use final_price for sorting
      const ap = Number(a?.final_price || a?.price || 0);
      const bp = Number(b?.final_price || b?.price || 0);
      const ar = Number(a?.rating?.rate || 0);
      const br = Number(b?.rating?.rate || 0);
      const ac = Number(a?.rating?.count || 0);
      const bc = Number(b?.rating?.count || 0);

      if (sort === "price_asc") return ap - bp;
      if (sort === "price_desc") return bp - ap;
      if (sort === "rating") return br - ar;
      return bc - ac || br - ar;
    });
  }, [data, activeCat, activeType, flashSaleOnly, query, sort]);

  const LoadingGrid = () => (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {Array.from({ length: 10 }).map((_, i) => (
        <div
          key={i}
          className="rounded-2xl border border-white/10 bg-white/[0.04] p-3"
        >
          <Skeleton height={140} borderRadius={16} />
          <div className="mt-3 space-y-2">
            <Skeleton height={12} width="90%" />
            <Skeleton height={10} width="60%" />
            <Skeleton height={16} width="45%" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <section className="min-h-screen bg-zinc-950 text-white">
      {/* soft glow */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-emerald-500/10 to-transparent" />

      <div className="relative mx-auto max-w-7xl px-4 pb-10 pt-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] text-zinc-200">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                Instant download • Pembayaran aman • Support WA/Email
              </div>

              <h2 className="mt-3 text-2xl font-extrabold tracking-tight sm:text-4xl">
                {STORE_NAME}
              </h2>
              <p className="mt-1 text-sm text-zinc-400 sm:text-base">
                Cari produk, filter kategori, dan urutkan.
              </p>
            </div>

            {/* Search + Sort */}
            <div className="w-full md:w-[520px]">
              <div className="grid gap-2 sm:grid-cols-[1fr,170px]">
                <div>
                  <label className="block text-xs font-semibold text-zinc-200 sm:text-sm">
                    Cari
                  </label>
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Jacket, Laptop, Necklace…"
                    className="mt-2 w-full rounded-2xl bg-white/[0.04] px-4 py-3 text-sm text-white ring-1 ring-white/10 outline-none placeholder:text-zinc-600 focus:ring-white/25"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-200 sm:text-sm">
                    Sort
                  </label>
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    className="mt-2 w-full rounded-2xl bg-white/[0.04] px-4 py-3 text-sm text-white ring-1 ring-white/10 outline-none focus:ring-white/25"
                  >
                    <option className="bg-zinc-900 text-white" value="popular">Terpopuler</option>
                    <option className="bg-zinc-900 text-white" value="rating">Rating</option>
                    <option className="bg-zinc-900 text-white" value="price_asc">Termurah</option>
                    <option className="bg-zinc-900 text-white" value="price_desc">Termahal</option>
                  </select>
                </div>
              </div>

              <div className="mt-2 text-sm text-zinc-500">
                Menampilkan{" "}
                <span className="font-semibold text-zinc-200">
                  {filteredProducts.length}
                </span>{" "}
                produk
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Category Filter */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-zinc-400">Kategori:</span>
              {categories.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setActiveCat(c.key)}
                  className={[
                    "rounded-full px-3 py-1.5 text-xs font-bold transition",
                    "ring-1 ring-white/10",
                    activeCat === c.key
                      ? "bg-white text-black"
                      : "bg-white/[0.04] text-zinc-200 hover:bg-white/10 hover:text-white",
                  ].join(" ")}
                >
                  {c.label}
                </button>
              ))}
            </div>

            {/* Type Filter */}
            <div className="flex flex-wrap items-center gap-2 border-l border-white/10 pl-3">
               <span className="text-xs font-semibold text-zinc-400">Tipe:</span>
               {[
                 { key: "all", label: "Semua" },
                 { key: "digital", label: "Digital" },
                 { key: "physical", label: "Fisik" }
               ].map((t) => (
                 <button
                    key={t.key}
                    onClick={() => setActiveType(t.key)}
                    className={[
                      "rounded-full px-3 py-1.5 text-xs font-bold transition",
                      "ring-1 ring-white/10",
                      activeType === t.key
                        ? "bg-indigo-500 text-white border-transparent"
                        : "bg-white/[0.04] text-zinc-200 hover:bg-white/10 hover:text-white",
                    ].join(" ")}
                 >
                   {t.label}
                 </button>
               ))}
            </div>

            {/* Flash Sale Filter */}
            <div className="flex items-center border-l border-white/10 pl-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={flashSaleOnly}
                  onChange={(e) => setFlashSaleOnly(e.target.checked)}
                  className="rounded border-white/10 bg-white/5 text-rose-500 focus:ring-rose-500/30"
                />
                <span className="text-xs font-bold text-rose-500">Flash Sale Promo</span>
              </label>
            </div>

            <button
              onClick={() => {
                setActiveCat("all");
                setActiveType("all");
                setFlashSaleOnly(false);
                setQuery("");
                setSort("popular");
              }}
              className="ml-auto rounded-full bg-white/[0.02] px-3 py-1.5 text-xs font-semibold text-zinc-300 ring-1 ring-white/10 hover:bg-white/[0.06] hover:text-white"
              title="Reset"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Divider */}
        <div className="mt-6 h-px w-full bg-white/10" />

        {/* Grid */}
        <div className="mt-6">
          {loading ? (
            <LoadingGrid />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {filteredProducts.map((product) => (
                <article
                  key={product.id}
                  className="group overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] transition hover:bg-white/[0.07]"
                >
                  <Link to={`/product/${product.id}`} className="block">
                    {/* Fixed image height = bikin rapi di HP */}
                    <div className="bg-white/[0.03] p-3">
                      <div className="h-[140px] sm:h-[170px] md:h-[190px] relative">
                        <img
                          src={product.image}
                          alt={product.title}
                          className="h-full w-full object-contain transition duration-200 group-hover:scale-[1.03]"
                          loading="lazy"
                        />
                        <button
                          onClick={(e) => toggleWishlist(product.id, e)}
                          className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 backdrop-blur-md opacity-0 group-hover:opacity-100 transition duration-300"
                        >
                          <svg className="w-4 h-4" 
                               fill={wishlistIds.includes(product.id) ? "#f43f5e" : "none"} 
                               stroke={wishlistIds.includes(product.id) ? "#f43f5e" : "white"} 
                               viewBox="0 0 24 24" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </Link>

                  {/* Body: pakai min-height biar semua card seragam */}
                  <div className="flex min-h-[160px] flex-col p-3">
                    <div className="flex items-start justify-between gap-2">
                      {/* Title height dikunci pakai line-clamp + leading */}
                      <Link
                        to={`/product/${product.id}`}
                        className="line-clamp-2 text-[13px] font-extrabold leading-[1.15] text-white/95 hover:underline hover:decoration-white/30 sm:text-sm"
                        title={product.title}
                      >
                        {product.title}
                      </Link>

                      <div className="flex flex-col items-end gap-1 shrink-0">
                         {product.active_flash_sale ? (
                           <>
                              <span className="text-[10px] text-zinc-500 line-through decoration-rose-500/50">
                                {formatRupiah(product.price)}
                              </span>
                              <span className="rounded-full bg-rose-500/15 px-2 py-1 text-[11px] font-extrabold text-rose-300 ring-1 ring-rose-500/20">
                                {formatRupiah(product.final_price)}
                              </span>
                           </>
                         ) : (
                           <span className="rounded-full bg-emerald-400/15 px-2 py-1 text-[11px] font-extrabold text-emerald-300 ring-1 ring-emerald-400/20">
                            {formatRupiah(product.final_price || product.price)}
                           </span>
                         )}
                      </div>
                    </div>

                    <div className="mt-1 text-[11px] font-semibold text-zinc-400">
                      {STORE_NAME}
                    </div>

                    <div className="mt-2 flex items-center justify-between gap-2 border-t border-white/5 pt-2">
                      <span className="inline-flex rounded-full bg-white/[0.04] px-2.5 py-1 text-[10px] font-semibold text-zinc-200 ring-1 ring-white/10">
                        {categoryLabel(product.category)}
                      </span>
                      {product.product_type === 'digital' && (
                        <span className="inline-flex rounded-full bg-indigo-500/10 px-2 py-1 text-[10px] font-semibold text-indigo-300 ring-1 ring-indigo-500/20">
                           Digital
                        </span>
                      )}
                    </div>

                    {/* Footer always at bottom */}
                    <div className="mt-auto pt-3">
                      <StarRow
                        rate={product?.rating?.rate ?? 0}
                        count={product?.rating?.count ?? 0}
                      />
                      <Link
                        to={`/product/${product.id}`}
                        className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-zinc-200 hover:text-white"
                      >
                        Lihat detail
                        <span className="transition group-hover:translate-x-0.5">→</span>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          {!loading && filteredProducts.length === 0 && (
            <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-center">
              <div className="text-base font-extrabold">Produk tidak ditemukan</div>
              <p className="mt-2 text-sm text-zinc-400">
                Coba ubah kata kunci/kategori atau klik reset.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Products;


// tambahkan cari produk aja yang bisa di filter berdasarkan nama produk